// Phase 6.D-9 — 레시피 / 메뉴 sub (메뉴추가 + 레시피추가 통합).
//
// 사용자 신고 1번 — "메뉴추가/레시피추가 sub 가 중복. 둘이 합쳐서 하나의 메뉴로":
//   기존 menus.tsx (회사 master + 행 편집) + add.tsx (검색·detail) 의 핵심을 합침.
//   - 검색 input
//   - 카테고리 그루핑 list
//   - "+ 메뉴 추가" 버튼 → MenuRecipeForm Modal (mode="menu", menu=null)
//   - 행 클릭 → MenuRecipeForm Modal (mode="menu", menu=row, recipes 포함)
//   - 행에 "매장 연결" badge — m.storeMenu 가 link 객체이거나 storeMenuLinks 길이 > 0
//
// 사용자 신고 2번 — "매장에서 메뉴 연결 등록했는데 미연결 표시":
//   백엔드 listMaster 가 storeListings 를 include 하지 않아 frontend 가 항상 미연결 보였음.
//   Phase 6.D-9 백엔드 fix 와 함께 — 본 페이지에서는 storeMenuLinks 길이 체크.
//
// Phase 6.D-11:
//   - 페이지 헤더에 "카테고리 관리" 버튼 추가 (CategoryManagerModal 재사용)
//   - 메뉴 행에 명시적 "편집"·"삭제" 버튼 (행 onClick 유지)
//   - 매장 연결 컬럼이 매장 이름 list 표시 (storeMenuLinks[].storeName)

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { menusControllerListMaster, menusControllerRemove } from '@dvvb/sdk';

import {
  AlertBanner,
  Badge,
  Button,
  EmptyState,
  Icon,
  TableHeader,
  TableRow,
  TableShell,
  useToast,
} from '../../../components/primitives';
import { unwrap, ApiCallError } from '../../../lib/envelope';
import { queryClient } from '../../../lib/queryClient';
import { useAuth } from '../../../auth/AuthContext';
import type { MenuRow } from '../../../types/api';
import {
  CategoryManagerModal,
  MenuRecipeForm,
  useInventoryMaterials,
  useMenuCategories,
  useMenuRecipes,
} from './_shared';

interface Envelope<T> {
  items: T[];
  count: number;
  limit: number;
  cursor: string | null;
  has_more: boolean;
}

const COLS = 'minmax(180px,1.6fr) 120px minmax(140px,1.2fr) 160px';

// Phase 6.D-9 — 백엔드 응답에 추가된 storeMenuLinks (활성 매장 link 배열).
// Phase 6.D-11 — storeName 추가 (link 매장 이름 list 표시).
// Phase 6.E — orphanedIngredientCount: 메뉴의 recipes 중 ingredient 가 BoxHero 끊긴 카운트.
type MasterMenuRow = MenuRow & {
  storeMenuLinks?: Array<{
    id: string;
    storeId: string;
    storeName: string | null;
    unitPrice: string | null;
    externalMenuId: string | null;
    isActive: boolean;
  }>;
  orphanedIngredientCount?: number;
};

export function SettingsRecipesMenusRoute() {
  const auth = useAuth();
  const isOwner = auth.role === 'owner';
  const toast = useToast();

  const [editing, setEditing] = useState<MenuRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  // Phase 6.D-11 — 카테고리 관리 모달
  const [manageCatOpen, setManageCatOpen] = useState(false);
  // Phase 6.D-11 — 삭제 진행 중인 메뉴 id (행 disable 표시)
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const menusQ = useQuery({
    queryKey: ['menus', 'master'],
    queryFn: async () => {
      const r = await menusControllerListMaster({
        query: { limit: '500' } as never,
      });
      return unwrap<Envelope<MasterMenuRow>>(r);
    },
  });

  // Phase 6.D-11 — 메뉴 삭제 (soft delete via menusControllerRemove)
  const deleteMu = useMutation({
    mutationFn: async (id: string) => {
      const r = await menusControllerRemove({ path: { id } });
      return unwrap(r);
    },
    onMutate: (id) => {
      setDeletingId(id);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['menus', 'master'] }),
        queryClient.invalidateQueries({ queryKey: ['menus', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['store-menus'] }),
      ]);
      toast.success('메뉴를 삭제했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '삭제 실패');
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  const inventoryMaterialsQ = useInventoryMaterials();
  const categoriesQ = useMenuCategories();
  const editingRecipesQ = useMenuRecipes(editing?.id ?? null);

  const inventory = inventoryMaterialsQ.data?.items ?? [];
  const categories = categoriesQ.data?.items ?? [];
  const menus: MasterMenuRow[] = menusQ.data?.items ?? [];

  // 검색 필터
  const filteredMenus = useMemo(() => {
    if (!debounced) return menus;
    return menus.filter(
      (m) =>
        m.name.toLowerCase().includes(debounced) ||
        (m.categoryName ?? '').toLowerCase().includes(debounced),
    );
  }, [menus, debounced]);

  // 카테고리 grouping
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; menus: MasterMenuRow[] }>();
    for (const m of filteredMenus) {
      const key = m.categoryId ?? '__none__';
      const label = m.categoryName ?? '카테고리 없음';
      if (!map.has(key)) map.set(key, { label, menus: [] });
      map.get(key)!.menus.push(m);
    }
    return Array.from(map.values());
  }, [filteredMenus]);

  if (menusQ.isLoading) {
    return (
      <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>
        메뉴 목록을 불러오는 중…
      </div>
    );
  }
  if (menusQ.isError) {
    return (
      <AlertBanner
        tone="danger"
        message={`메뉴 목록을 불러오지 못했습니다: ${(menusQ.error as ApiCallError).message ?? '알 수 없는 오류'}`}
      />
    );
  }

  const headerRow = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-weak)',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 200 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-1)' }}>
          회사 메뉴 ({filteredMenus.length}
          {debounced && menus.length !== filteredMenus.length ? ` / ${menus.length}` : ''}개)
        </div>
        <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 2 }}>
          메뉴와 레시피를 한 번에 등록·편집합니다. 행 클릭 = 편집. 매장별 노출은 [매장분류] 탭에서.
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            border: '1px solid var(--border-base)',
            borderRadius: 8,
            minWidth: 220,
          }}
        >
          <Icon name="search" size={14} color="var(--fg-3)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="메뉴명 / 카테고리 검색"
            style={{
              border: 'none',
              outline: 'none',
              flex: 1,
              fontSize: 13,
              background: 'transparent',
              fontFamily: 'var(--font-sans)',
              color: 'var(--fg-1)',
            }}
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={!isOwner}
          title={!isOwner ? 'owner 만 카테고리를 관리할 수 있습니다' : ''}
          onClick={() => setManageCatOpen(true)}
        >
          카테고리 관리
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={!isOwner}
          title={!isOwner ? 'owner 만 메뉴를 추가할 수 있습니다' : ''}
          onClick={() => setCreating(true)}
        >
          + 메뉴 추가
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-weak)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        {headerRow}
        <div style={{ padding: 16 }}>
          {menus.length === 0 ? (
            <EmptyState
              icon="receipt"
              title="등록된 메뉴가 없습니다"
              description="회사 메뉴와 레시피를 한 번에 등록하세요."
              action={
                isOwner ? (
                  <Button variant="primary" onClick={() => setCreating(true)}>
                    + 메뉴 추가
                  </Button>
                ) : null
              }
            />
          ) : filteredMenus.length === 0 ? (
            <div
              style={{
                padding: '32px 12px',
                textAlign: 'center',
                color: 'var(--fg-3)',
                fontSize: 13,
              }}
            >
              검색 결과가 없습니다.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {grouped.map((g, gi) => (
                <div key={gi} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--fg-3)',
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-wide)',
                      padding: '0 8px',
                    }}
                  >
                    {g.label} · {g.menus.length}개
                  </div>
                  <TableShell>
                    <TableHeader columns={COLS}>
                      <div>메뉴명</div>
                      <div style={{ textAlign: 'right' }}>기본가</div>
                      <div>매장 연결</div>
                      <div style={{ textAlign: 'right' }}>관리</div>
                    </TableHeader>
                    {g.menus.map((m, i) => {
                      const links = m.storeMenuLinks ?? [];
                      const linkCount = links.length || (m.storeMenu ? 1 : 0);
                      const storeNames = links
                        .map((l) => l.storeName)
                        .filter((n): n is string => !!n);
                      const storesLabel =
                        storeNames.length > 0
                          ? storeNames.join(', ')
                          : linkCount > 0
                            ? `${linkCount}개 매장`
                            : '미연결';
                      const isDeleting = deletingId === m.id;
                      return (
                        <TableRow
                          key={m.id}
                          columns={COLS}
                          first={i === 0}
                          onClick={
                            isOwner && !isDeleting ? () => setEditing(m) : undefined
                          }
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                minWidth: 0,
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: 'var(--fg-1)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  minWidth: 0,
                                  flex: '0 1 auto',
                                }}
                              >
                                {m.name}
                              </div>
                              {/* Phase 6.E — 끊긴 재료 N개 badge */}
                              {(m.orphanedIngredientCount ?? 0) > 0 && (
                                <Badge
                                  variant="warning"
                                  style={{
                                    flex: '0 0 auto',
                                    fontSize: 10,
                                    padding: '1px 7px',
                                  }}
                                >
                                  끊긴 재료 {m.orphanedIngredientCount}개
                                </Badge>
                              )}
                            </div>
                            {m.description && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: 'var(--fg-3)',
                                  marginTop: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {m.description}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              textAlign: 'right',
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            ₩{Math.round(Number(m.unitPrice ?? 0)).toLocaleString('ko-KR')}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: linkCount > 0 ? 'var(--success)' : 'var(--fg-3)',
                              fontWeight: linkCount > 0 ? 600 : 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={storesLabel}
                          >
                            {storesLabel}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 4,
                              justifyContent: 'flex-end',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!isOwner || isDeleting}
                              onClick={() => setEditing(m)}
                            >
                              편집
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={!isOwner || isDeleting}
                              title={!isOwner ? 'owner 만 메뉴를 삭제할 수 있습니다' : ''}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `"${m.name}" 메뉴를 삭제하시겠습니까?\n\n매장 연결도 함께 비활성화됩니다.`,
                                  )
                                ) {
                                  deleteMu.mutate(m.id);
                                }
                              }}
                              style={{ color: 'var(--danger)' }}
                            >
                              {isDeleting ? '삭제 중…' : '삭제'}
                            </Button>
                          </div>
                        </TableRow>
                      );
                    })}
                  </TableShell>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <MenuRecipeForm
        open={creating}
        onClose={() => setCreating(false)}
        menu={null}
        inventory={inventory}
        categories={categories}
        initialRecipes={[]}
        mode="menu"
      />

      {/* Edit Modal */}
      <MenuRecipeForm
        open={!!editing}
        onClose={() => setEditing(null)}
        menu={editing}
        inventory={inventory}
        categories={categories}
        initialRecipes={editingRecipesQ.data?.items ?? []}
        mode="menu"
      />

      {/* Phase 6.D-11 — Category Manager Modal */}
      <CategoryManagerModal
        open={manageCatOpen}
        onClose={() => setManageCatOpen(false)}
        categories={categories}
      />
    </>
  );
}
